/**
 * Error store for global error state management
 */

import { create } from 'zustand';

export type ErrorType =
  | 'network'      // Server unreachable
  | 'timeout'      // Request timed out
  | 'auth'         // 401 - Session expired
  | 'server'       // 500+ errors
  | 'client'       // 4xx errors (not 401)
  | 'llm'          // LLM-specific errors (timeout, rate limit)
  | 'unknown';

export interface AppError {
  id: string;
  type: ErrorType;
  message: string;
  details?: string;
  timestamp: number;
  dismissable: boolean;
  action?: {
    label: string;
    handler: () => void;
  };
}

interface ErrorState {
  errors: AppError[];
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => string;
  removeError: (id: string) => void;
  clearErrors: () => void;
  clearErrorsByType: (type: ErrorType) => void;
}

export const useErrorStore = create<ErrorState>((set) => ({
  errors: [],

  addError: (error) => {
    const id = crypto.randomUUID();
    const fullError: AppError = {
      ...error,
      id,
      timestamp: Date.now(),
    };

    set((state) => {
      // Deduplicate auth errors - only show one at a time
      if (error.type === 'auth' && state.errors.some((e) => e.type === 'auth')) {
        return state;
      }
      return { errors: [...state.errors, fullError] };
    });

    // Auto-dismiss after 10 seconds for dismissable errors
    if (error.dismissable) {
      setTimeout(() => {
        set((state) => ({
          errors: state.errors.filter((e) => e.id !== id),
        }));
      }, 10000);
    }

    return id;
  },

  removeError: (id) =>
    set((state) => ({
      errors: state.errors.filter((e) => e.id !== id),
    })),

  clearErrors: () => set({ errors: [] }),

  clearErrorsByType: (type) =>
    set((state) => ({
      errors: state.errors.filter((e) => e.type !== type),
    })),
}));

/**
 * Helper to create user-friendly error messages in Serbian
 */
export function getErrorMessage(type: ErrorType, details?: string): string {
  switch (type) {
    case 'network':
      return 'Server nije dostupan. Proveri internet konekciju.';
    case 'timeout':
      return 'Zahtev je trajao predugo. Pokušaj ponovo.';
    case 'auth':
      return 'Sesija je istekla. Uloguj se ponovo.';
    case 'server':
      return 'Greška na serveru. Pokušaj ponovo za koji minut.';
    case 'llm':
      return details || 'AI asistent trenutno nije dostupan. Pokušaj ponovo.';
    case 'client':
      return details || 'Došlo je do greške. Pokušaj ponovo.';
    default:
      return details || 'Neočekivana greška. Pokušaj ponovo.';
  }
}

export default useErrorStore;
